import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';

const formatAttendance = (value = '') =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const CommunityMeetupRegistrations = () => {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [message, setMessage] = useState('');
  const [sortModel, setSortModel] = useState([{ field: 'registeredAt', sort: 'desc' }]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const fetchRegistrations = useCallback(() => {
    setLoading(true);
    setMessage('');
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/community-meetup-registrations`, { headers })
      .then((res) => {
        setRegistrations(Array.isArray(res.data?.registrations) ? res.data.registrations : []);
      })
      .catch((err) => {
        console.error('Error loading community meetup registrations', err);
        setMessage(err?.response?.data?.message || 'Unable to load meetup registrations.');
      })
      .finally(() => setLoading(false));
  }, [headers]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  const filteredRows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return registrations;
    return registrations.filter((row) =>
      [
        row.fullName,
        row.email,
        row.phone,
        row.cityZip,
        row.primaryInterest,
        row.attendanceType,
        row.meetupTitle,
        row.meetupCity,
        row.meetupState,
        row.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [filter, registrations]);

  const exportCsv = () => {
    const headers = [
      'Registered At',
      'Meetup',
      'Meetup Date',
      'Name',
      'Email',
      'Phone',
      'City/ZIP',
      'Attendance',
      'Interest',
      'Status',
      'Notes',
    ];
    const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [
      headers.map(escapeCell).join(','),
      ...filteredRows.map((row) =>
        [
          row.registeredAt,
          row.meetupTitle,
          row.meetupDate,
          row.fullName,
          row.email,
          row.phone,
          row.cityZip,
          formatAttendance(row.attendanceType),
          row.primaryInterest,
          row.status,
          row.notes,
        ]
          .map(escapeCell)
          .join(',')
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `uc-community-meetup-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      field: 'registeredAt',
      headerName: 'Registered',
      minWidth: 170,
      flex: 0.7,
    },
    {
      field: 'meetupTitle',
      headerName: 'Meetup',
      minWidth: 260,
      flex: 1.2,
      renderCell: (params) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: '#0f172a', fontWeight: 900, lineHeight: 1.25 }}>
            {params.value || 'UC Community Meetup'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
            {params.row.meetupDate || 'Date TBA'} · {params.row.meetupCity || 'City TBA'}
            {params.row.meetupState ? `, ${params.row.meetupState}` : ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'fullName',
      headerName: 'Registrant',
      minWidth: 220,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: '#0f172a', fontWeight: 900 }}>
            {params.value}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            {params.row.email}
          </Typography>
        </Box>
      ),
    },
    { field: 'phone', headerName: 'Phone', minWidth: 140, flex: 0.6 },
    { field: 'cityZip', headerName: 'City / ZIP', minWidth: 150, flex: 0.7 },
    {
      field: 'attendanceType',
      headerName: 'Attendance',
      minWidth: 160,
      flex: 0.7,
      renderCell: (params) => <Chip size="small" label={formatAttendance(params.value)} variant="outlined" />,
    },
    {
      field: 'primaryInterest',
      headerName: 'Interest',
      minWidth: 230,
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 130,
      flex: 0.55,
      renderCell: (params) => (
        <Chip
          size="small"
          label={formatAttendance(params.value || 'registered')}
          color={params.value === 'registered' ? 'success' : 'default'}
        />
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Meetup Registrations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review people who registered for UC city-center, library, and online community meetups.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`${registrations.length} registrations`} color="primary" variant="outlined" />
          <Button variant="outlined" onClick={fetchRegistrations}>Refresh</Button>
          <Button variant="contained" onClick={exportCsv} disabled={!filteredRows.length}>Export CSV</Button>
        </Stack>
      </Box>

      {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}

      <Paper elevation={0} sx={{ p: 2, border: '1px solid #dbeafe', borderRadius: 3 }}>
        <TextField
          fullWidth
          size="small"
          label="Search registrations"
          placeholder="Search name, email, meetup, interest, city, phone..."
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          sx={{ mb: 2 }}
        />

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 'clamp(520px, 62vh, 720px)', width: '100%' }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row.id}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              disableRowSelectionOnClick
              rowHeight={72}
              columnHeaderHeight={56}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 3,
                '& .MuiDataGrid-columnHeaders': {
                  background: 'linear-gradient(180deg, #f8fafc 0%, #eef7f6 100%)',
                  color: '#0f172a',
                },
                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 950 },
                '& .MuiDataGrid-row:hover': { bgcolor: '#f0fdfa' },
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CommunityMeetupRegistrations;
